<template>
  <div>
    <FlashMessage v-if="messages.text !== ''" :message="messages" />

    <div class="min-h-screen bg-gray-100 p-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center items-center h-64">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p class="mt-4 text-gray-600">Loading filters...</p>
        </div>
      </div>

      <div v-else>
        <!-- Display Filter Data Summary -->
        <div v-if="filterData" class="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 class="font-semibold text-blue-800 mb-2">Campaign Filter Summary</h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div v-if="filterData.batch_json && filterData.batch_json.length">
              <span class="text-gray-600">Batches:</span>
              <span class="ml-2 font-medium">{{ filterData.batch_json.map(b => b.year).join(', ') }}</span>
            </div>
            <div v-if="filterData.program_json && filterData.program_json.length">
              <span class="text-gray-600">Programs:</span>
              <span class="ml-2 font-medium">{{ filterData.program_json.map(p => p.name).join(', ') }}</span>
            </div>
            <div v-if="filterData.department_json && filterData.department_json.length">
              <span class="text-gray-600">Departments:</span>
              <span class="ml-2 font-medium">{{ filterData.department_json.map(d => d.name).join(', ') }}</span>
            </div>
            <div v-if="filterData.semester_json && filterData.semester_json.length">
              <span class="text-gray-600">Semesters:</span>
              <span class="ml-2 font-medium">{{ filterData.semester_json.map(s => s.semester).join(', ') }}</span>
            </div>
          </div>
        </div>

        <!-- Top Section -->
        <div class="flex justify-end pb-5 gap-3">
          <button class="px-6 py-2.5 text-gray-500 bg-white text-sm font-semibold shadow-sm rounded-xl">
            {{ Totalcount.count || 0 }} Total
          </button>
          <button @click="clearFilters" class="flex items-center gap-1 px-3 py-2.5 rounded text-sm font-semibold transition-colors hover:underline text-red-400 hover:text-red-500">
            Clear all
          </button>
        </div>

        <!-- FILTER GRID -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <!-- BATCH -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-[300px] overflow-y-auto">
            <h3 class="font-bold text-sm text-gray-800 mb-3 tracking-wide">BATCH</h3>
            <div class="space-y-1.5">
              <label v-for="item in batchs" :key="item.id" class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" :value="item.id" v-model="filters.batch" class="w-3.5 h-3.5 rounded border-gray-300 text-blue-600" />
                <span class="text-sm text-gray-700">{{ item.year }}</span>
              </label>
            </div>
          </div>

          <!-- PROGRAM -->
          <div class="rounded-xl shadow-sm border border-gray-200 p-4 transition overflow-y-auto" :class="!filters.batch.length ? 'bg-gray-300 opacity-60 pointer-events-none' : 'bg-white'">
            <h3 class="font-bold text-sm text-gray-800 mb-3 tracking-wide">PROGRAM</h3>
            <div class="space-y-1.5">
              <label v-for="item in programs" :key="item.id" class="flex items-center gap-2">
                <input type="checkbox" :value="item.id" v-model="filters.program" :disabled="!filters.batch.length" class="w-3.5 h-3.5 rounded border-gray-300 text-blue-600" />
                <span class="text-sm text-gray-700">{{ item.name }}</span>
              </label>
            </div>
          </div>

          <!-- DEPARTMENT -->
          <div class="rounded-xl shadow-sm border border-gray-200 p-4 transition overflow-y-auto hidden" :class="!filters.batch.length ? 'bg-gray-300 opacity-60 pointer-events-none' : 'bg-white'">
            <h3 class="font-bold text-sm text-gray-800 mb-3 tracking-wide">DEPARTMENT</h3>
            <div class="space-y-1.5">
              <label v-for="item in departments" :key="item.id" class="flex items-center gap-2">
                <input type="checkbox" :value="item.id" v-model="filters.department" :disabled="!filters.batch.length" class="w-3.5 h-3.5 rounded border-gray-300 text-blue-600" />
                <span class="text-sm text-gray-700">{{ item.name }}</span>
              </label>
            </div>
          </div>

          <!-- SEMESTER -->
          <div class="rounded-xl shadow-sm border border-gray-200 p-4 transition overflow-y-auto" :class="!filters.batch.length ? 'bg-gray-300 opacity-60 pointer-events-none' : 'bg-white'">
            <h3 class="font-bold text-sm text-gray-800 mb-3 tracking-wide">SEMESTER</h3>
            <div class="space-y-1.5">
              <label v-for="item in semsters" :key="item.id" class="flex items-center gap-2">
                <input type="checkbox" :value="item.id" v-model="filters.semester" :disabled="!filters.batch.length" class="w-3.5 h-3.5 rounded border-gray-300 text-blue-600" />
                <span class="text-sm text-gray-700">{{ item.semester }} ({{ item.department?.name || 'N/A' }})</span>
              </label>
            </div>
          </div>
        </div>

        <!-- BUTTON SECTION -->
        <div class="flex flex-col md:flex-row gap-4 justify-between mt-6">
          <div class="flex gap-3">
            <button @click="goBackToStep2" class="px-6 py-2.5 rounded text-white bg-[#1a2e5a] hover:bg-hover text-sm font-semibold">
              Previous
            </button>
            <button @click="handleTestRun" class="px-6 py-2.5 rounded text-white bg-blue-500 hover:bg-hover text-sm font-semibold">
              Test Run
            </button>
          </div>
          <button v-if="filters.batch.length > 0" @click="handleSave" :disabled="disable_submit"
            class="rounded-lg text-sm px-6 py-2 flex items-center gap-2 transition-all"
            :class="disable_submit ? 'bg-gray-300 cursor-not-allowed text-black' : 'bg-[#1a2e5a] hover:bg-hover text-white'">
            <span v-if="disable_submit" class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
            {{ disable_submit ? "Submitting..." : "Submit" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FlashMessage from "@/components/FlashMessage.vue";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "FilterPanel",
  data() {
    return {
      filters: { batch: [], program: [], semester: [], department: [] },
      messages: { text: "", type: "null" },
      batchs: [],
      programs: [],
      departments: [],
      semsters: [],
      Totalcount: 0,
      disable_submit: false,
      isLoading: true,
    };
  },
  props: {
    campaignSlug: { type: String, required: true },
    campaignData: { type: Object, default: null },
    filterData: { type: Object, default: null }, // Add filterData prop
  },
  computed: { ...mapGetters(["userInfo"]) },
  components: { FlashMessage },
  async created() {
    await this.loadAllData();
  },
  watch: {
    filters: {
      handler() { this.FilterCount(); },
      deep: true,
    },
    "filters.batch"(newVal) {
      if (newVal.length === 0) {
        this.filters.program = [];
        this.filters.department = [];
        this.filters.semester = [];
      }
    },
    // Watch for filterData changes from parent
    filterData: {
      handler(newData) {
        if (newData) {
          this.loadExistingFilters();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async loadAllData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.batchGet(),
          this.programGet(),
          this.departmentGet(),
          this.semesterGet(),
        ]);
        this.loadExistingFilters();
        await this.FilterCount();
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        this.isLoading = false;
      }
    },

    loadExistingFilters() {
      // First try to load from filterData (fetched from API)
      if (this.filterData) {
        if (this.filterData.batch_json && this.filterData.batch_json.length > 0) {
          this.filters.batch = this.filterData.batch_json.map(b => b.id);
        }
        if (this.filterData.program_json && this.filterData.program_json.length > 0) {
          this.filters.program = this.filterData.program_json.map(p => p.id);
        }
        if (this.filterData.department_json && this.filterData.department_json.length > 0) {
          this.filters.department = this.filterData.department_json.map(d => d.id);
        }
        if (this.filterData.semester_json && this.filterData.semester_json.length > 0) {
          this.filters.semester = this.filterData.semester_json.map(s => s.id);
        }
      } 
      // Fallback to campaignData if filterData is not available
      else if (this.campaignData) {
        if (this.campaignData.batch_json && this.campaignData.batch_json.length > 0) {
          this.filters.batch = this.campaignData.batch_json.map(b => b.id);
        }
        if (this.campaignData.program_json && this.campaignData.program_json.length > 0) {
          this.filters.program = this.campaignData.program_json.map(p => p.id);
        }
        if (this.campaignData.department_json && this.campaignData.department_json.length > 0) {
          this.filters.department = this.campaignData.department_json.map(d => d.id);
        }
        if (this.campaignData.semester_json && this.campaignData.semester_json.length > 0) {
          this.filters.semester = this.campaignData.semester_json.map(s => s.id);
        }
      }
    },

    goBackToStep2() {
      this.$emit('goBack2');
    },

    clearFilters() {
      this.filters.batch = [];
      this.filters.program = [];
      this.filters.semester = [];
      this.filters.department = [];
    },

    async FilterCount() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/user-data-count/`, {
          params: {
            batch: this.filters.batch.join(","),
            program: this.filters.program.join(","),
            semester: this.filters.semester.join(","),
            department: this.filters.department.join(","),
            Key: this.userInfo.token_key,
          },
        });
        this.Totalcount = response.data.status === 200 ? response.data : 0;
      } catch (error) {
        console.error("Filter API failed:", error);
        this.Totalcount = 0;
      }
    },

    async batchGet() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/batch-list/?Key=${this.userInfo.token_key}`);
        this.batchs = response.data.status === 200 ? response.data.data : [];
      } catch (error) {
        console.error("Batch API failed:", error);
        this.batchs = [];
      }
    },

    async programGet() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/program-list/?Key=${this.userInfo.token_key}`);
        this.programs = response.data.status === 200 ? response.data.data : [];
      } catch (error) {
        console.error("Program API failed:", error);
        this.programs = [];
      }
    },

    async departmentGet() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/department-list/?Key=${this.userInfo.token_key}`);
        this.departments = response.data.status === 200 ? response.data.data : [];
      } catch (error) {
        console.error("Department API failed:", error);
        this.departments = [];
      }
    },

    async semesterGet() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/semetser-get/?Key=${this.userInfo.token_key}`);
        this.semsters = response.data.status === 200 ? response.data.data : [];
      } catch (error) {
        console.error("Semester API failed:", error);
        this.semsters = [];
      }
    },

    async handleSave() {
      if (this.disable_submit) return;
      this.disable_submit = true;
      try {
        const response = await axios.put(`${process.env.VUE_APP_API}/email-campaign/send/${this.campaignSlug}/`, {
          batch_json: this.filters.batch,
          department_json: this.filters.department,
          program_json: this.filters.program,
          semester_json: this.filters.semester,
          step_3: true,
        }, { params: { Key: this.userInfo.token_key } });

        if (response.data.status === 200) {
          this.messages = { text: response.data.message, type: "success" };
          setTimeout(() => { this.$router.push({ name: "EmailLogs" }); }, 1500);
        } else {
          this.messages = { text: "Submit Failed", type: "error" };
        }
      } catch (err) {
        console.error(err);
        this.messages = { text: "Something went wrong", type: "error" };
      }
      setTimeout(() => {
        this.messages = { text: "", type: null };
        this.disable_submit = false;
      }, 1500);
    },

    async handleTestRun() {
      try {
        const response = await axios.put(`${process.env.VUE_APP_API}/email-campaign/send/${this.campaignSlug}/`, {
          user_ids: [this.userInfo.user.id],
        }, { params: { Key: this.userInfo.token_key } });

        this.messages = {
          text: response.data.status === 200 ? response.data.message : "Test Run Failed",
          type: response.data.status === 200 ? "success" : "error",
        };
      } catch (err) {
        console.error(err);
        this.messages = { text: "Something went wrong", type: "error" };
      }
      setTimeout(() => { this.messages = { text: "", type: null }; }, 1500);
    },
  },
};
</script>